import React, { useState, useEffect, useCallback } from 'react';
import { MarketingBanner } from '../MarketingBanner';
import type { BannerData } from '../MarketingBanner';
import type { ResultSectionConfig } from '../../utils/types';
import { getCachedBanners, getApiBase } from '../../utils/api';
import { getSessionId } from '../../hooks/useSearch';

async function trackImpression(bannerId: string, campaignId: string): Promise<void> {
  try {
    await fetch(`${getApiBase()}/marketing/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: getSessionId(),
        type: 'impression',
        productId: bannerId,
        metadata: { campaignId },
      }),
    });
  } catch { /* silent */ }
}

interface InlineBannerWidgetProps {
  config: ResultSectionConfig;
}

export const InlineBannerWidget: React.FC<InlineBannerWidgetProps> = ({ config }) => {
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    getCachedBanners().then((all) => all.filter((b: any) => b.position === 'inline')).then((data) => {
      const limited = config.maxItems ? data.slice(0, config.maxItems) : data;
      setBanners(limited);
    });
  }, [config.maxItems]);

  const handleDismiss = useCallback((bannerId: string) => {
    setDismissed((prev) => new Set([...prev, bannerId]));
  }, []);

  const handleImpression = useCallback((bannerId: string, campaignId: string) => {
    trackImpression(bannerId, campaignId);
  }, []);

  const visible = banners.filter((b) => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  return (
    <div className="my-4">
      {visible.map((banner) => (
        <MarketingBanner
          key={banner.id}
          banner={banner}
          onDismiss={handleDismiss}
          onImpression={handleImpression}
        />
      ))}
    </div>
  );
};
